import React from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Alert } from 'reactstrap';
import { useNavigate } from 'react-router-dom';

interface ClockErrorModalProps {
  isOpen: boolean;
  toggle: () => void;
  error: string;
  details?: string;
  unconvertedClock?: any;
  manualEntriesExist?: boolean;
  manualMinutes?: number;
  clockMinutes?: number;
  difference?: number;
  canAutoConvert?: boolean;
}

const ClockErrorModal: React.FC<ClockErrorModalProps> = ({
  isOpen,
  toggle,
  error,
  details,
  unconvertedClock,
  manualEntriesExist,
  manualMinutes,
  clockMinutes,
  difference,
  canAutoConvert
}) => {
  const navigate = useNavigate();

  const handleNavigateToConvert = () => {
    toggle();
    if (unconvertedClock) {
      navigate('/time-entries/convert-clock', { 
        state: { clockData: unconvertedClock } 
      });
    }
  };

  const handleContactSupport = () => {
    toggle();
    navigate('/support');
  };

  const formatMinutesToTime = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}min`;
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>
        <i className="ri-error-warning-line text-warning me-2"></i>
        Clock kann nicht gestartet werden
      </ModalHeader>
      <ModalBody>
        <Alert color="danger" className="mb-3">
          <h5 className="alert-heading">{error}</h5>
          {details && (
            <p className="mb-0 mt-2">{details}</p>
          )}
        </Alert>

        {unconvertedClock && (
          <div className="mb-4">
            <h6 className="mb-3">Details zur nicht konvertierten Clock:</h6>
            <div className="table-responsive">
              <table className="table table-bordered">
                <tbody>
                  <tr>
                    <td width="40%"><strong>Datum:</strong></td>
                    <td>{new Date(unconvertedClock.date).toLocaleDateString('de-DE')}</td>
                  </tr>
                  <tr>
                    <td><strong>Clock-In:</strong></td>
                    <td>{new Date(unconvertedClock.clock_in).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}</td>
                  </tr>
                  <tr>
                    <td><strong>Clock-Out:</strong></td>
                    <td>{unconvertedClock.clock_out ? new Date(unconvertedClock.clock_out).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) : '-'}</td>
                  </tr>
                  <tr>
                    <td><strong>Gesamtzeit:</strong></td>
                    <td>{clockMinutes ? formatMinutesToTime(clockMinutes) : formatMinutesToTime(unconvertedClock.total_minutes || 0)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {manualEntriesExist && (
          <div className="mb-3">
            <Alert color="warning">
              <h6 className="alert-heading mb-2">
                <i className="ri-information-line me-2"></i>
                Manuelle Zeiteinträge gefunden
              </h6>
              <p className="mb-2">
                Es wurden bereits manuelle Zeiteinträge für diesen Tag erfasst:
              </p>
              <ul className="mb-0">
                <li>Manuelle Einträge: {manualMinutes ? formatMinutesToTime(manualMinutes) : '0h 0min'}</li>
                <li>Clock-Zeit: {clockMinutes ? formatMinutesToTime(clockMinutes) : '0h 0min'}</li>
                {difference !== undefined && (
                  <li>Differenz: {formatMinutesToTime(difference)}</li>
                )}
              </ul>
              {difference && difference > 60 && (
                <p className="mb-0 mt-2 text-danger">
                  <strong>Die Differenz ist zu groß!</strong> Bitte konvertieren Sie die Clock korrekt oder kontaktieren Sie den Support.
                </p>
              )}
            </Alert>
          </div>
        )}

        <div className="bg-light p-3 rounded">
          <h6 className="mb-3">Was können Sie tun?</h6>
          <ol className="mb-0">
            <li className="mb-2">
              <strong>Clock konvertieren:</strong> Wandeln Sie die nicht konvertierte Clock in Zeiteinträge um.
            </li>
            {manualEntriesExist && (
              <li className="mb-2">
                <strong>Manuelle Einträge prüfen:</strong> Überprüfen Sie, ob die manuellen Einträge korrekt sind.
              </li>
            )}
            <li className="mb-2">
              <strong>Support kontaktieren:</strong> Wenn Sie Hilfe benötigen, wenden Sie sich an den Support.
            </li>
          </ol>
        </div>
      </ModalBody>
      <ModalFooter>
        {unconvertedClock && !manualEntriesExist && (
          <Button color="primary" onClick={handleNavigateToConvert}>
            <i className="ri-time-line me-2"></i>
            Zur Clock-Konvertierung
          </Button>
        )}
        {manualEntriesExist && (
          <Button color="warning" onClick={() => navigate('/time-entries')}>
            <i className="ri-file-list-3-line me-2"></i>
            Zeiteinträge prüfen
          </Button>
        )}
        <Button color="info" onClick={handleContactSupport}>
          <i className="ri-customer-service-2-line me-2"></i>
          Support kontaktieren
        </Button>
        <Button color="secondary" onClick={toggle}>
          Schließen
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ClockErrorModal;